import { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact, type AgGridReactProps } from "ag-grid-react";
import {
  type ColDef,
  type GridReadyEvent,
  type GridApi,
  ModuleRegistry,
  AllCommunityModule
} from "ag-grid-community";
import { cx } from "shared/utils";
import { Show } from "shared/components/helpers/Show";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Loader } from "shared/components/Loader";

import "./ag-theme-boom.css";

const DEFAULT_PER_PAGE = Number(window.localStorage.getItem("per_page") || 10);

ModuleRegistry.registerModules([AllCommunityModule]);

const DEFAULT_ROW_HEIGHT = 64;
const DEFAULT_HEADER_HEIGHT = 44;

export const DEFAULT_AG_GRID_VIEWPORT_OFFSET = 200;

type AgGridThemeOverrideKey =
  | "--ag-background-color"
  | "--ag-header-background-color"
  | "--ag-row-hover-color"
  | "--ag-border-color"
  | "--ag-row-border-color"
  | "--ag-font-size"
  | "--ag-header-font-size"
  | "--ag-header-font-weight"
  | "--ag-header-font-color"
  | "--ag-cell-horizontal-padding"
  | "--ag-selected-row-background-color"
  | "--ag-range-selection-border-color"
  | "--ag-column-hover-color"
  | "--ag-foreground-color";

type AgGridThemeOverrides = Partial<Record<AgGridThemeOverrideKey, string>>;

export function AgGrid<T extends { id?: string | number }>({
  columns,
  rows,
  rowHeight = DEFAULT_ROW_HEIGHT,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  wrapperClassName,
  className,
  footerClassName,
  themeOverrides,
  loading,
  footer,
  showFooter = false,
  perPageRows = DEFAULT_PER_PAGE,
  viewPortHeight = window.innerHeight,
  viewPortHeightOffset = 0,
  noRowsFallback,
  getRowHeight,
  getRowId,
  isFullWidthRow,
  fullWidthCellRenderer,
  onGridReady: onGridReadyProp,
  ...restProps
}: Omit<AgGridReactProps<T>, "rowData" | "columnDefs" | "className"> & {
  columns: ColDef<T>[];
  rows: T[];
  rowHeight?: number;
  headerHeight?: number;
  wrapperClassName?: string;
  className?: string;
  footerClassName?: string;
  themeOverrides?: AgGridThemeOverrides;
  loading?: boolean;
  footer?: React.ReactNode;
  showFooter?: boolean;
  perPageRows?: number;
  noRowsFallback?: React.ReactNode;
  viewPortHeight?: number;
  viewPortHeightOffset?: number;
}) {
  const gridRef = useRef<AgGridReact<T>>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (footerRef.current)
      setFooterHeight(footerRef.current.getBoundingClientRect().height);
  }, [footer]);

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    onGridReadyProp?.(params);
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      suppressMovable: true,
      resizable: false,
      sortable: false,
      unSortIcon: true
    }),
    []
  );

  const gridHeight = useMemo(() => {
    const contentHeight =
      rowHeight * (rows.length || perPageRows) + headerHeight + 2; // borders
    const maxHeight = viewPortHeight - viewPortHeightOffset - footerHeight;
    return Math.min(contentHeight, maxHeight);
  }, [
    rows.length,
    perPageRows,
    rowHeight,
    headerHeight,
    viewPortHeight,
    viewPortHeightOffset,
    footerHeight
  ]);

  const showEmptyState = !loading && rows.length === 0;

  const themeStyle = useMemo(() => {
    if (!themeOverrides || Object.keys(themeOverrides).length === 0)
      return undefined;
    return themeOverrides;
  }, [themeOverrides]);

  return (
    <div
      className={cx(
        "flex flex-col rounded-lg border border-alpha-100 overflow-hidden bg-background-1000",
        wrapperClassName
      )}
      style={{ contain: "inline-size" }}
    >
      <div
        className={cx("ag-theme-boom", className)}
        style={{
          height: showEmptyState ? "auto" : gridHeight,
          ...themeStyle
        }}
      >
        {showEmptyState ? (
          (noRowsFallback ?? <NoRowsFallback />)
        ) : (
          <AgGridReact<T>
            ref={gridRef}
            rowData={loading ? null : rows}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            loadingOverlayComponent={() => <Loader />}
            getRowHeight={getRowHeight}
            getRowId={getRowId}
            isFullWidthRow={isFullWidthRow}
            fullWidthCellRenderer={fullWidthCellRenderer}
            onGridReady={onGridReady}
            suppressCellFocus
            animateRows={false}
            loading={loading}
            overlayNoRowsTemplate="<div></div>"
            {...restProps}
          />
        )}
      </div>

      <Show if={showFooter && footer && !showEmptyState}>
        <div
          ref={footerRef}
          className={cx(
            "border-t border-alpha-40 px-12 py-3 text-content-1000",
            footerClassName
          )}
        >
          {footer}
        </div>
      </Show>
    </div>
  );
}
